import { useState, useEffect } from "react";
import { Modal, Form, Button, Alert } from "react-bootstrap";
import Swal from "sweetalert2";
import { editarPaiVacunacion, activarMatrizAutomatica, subirFotoVivienda, adjuntarFotoCarnet } from "../api/bacSarampionAPI.js";
import { comprimirImagen, blobABase64 } from "../utils/imageCompression";
import { obtenerCatalogosCacheados } from "../api/catalogosCache";
import { useBacAuth } from "../../context/BacAuthContext";
import API from "../../axios.jsx";

const MOTIVOS_SIMPLE = [
  { value: "contraindicacion", label: "Contraindicación médica" },
  { value: "factores_salud",   label: "Factores R/C servicio de salud" },
  { value: "factores_familia", label: "Factores R/C la familia" },
  { value: "sin_equipo",       label: "No hay equipo vacunador" },
  { value: "no_aplica",        label: "No aplica" },
  { value: "otros",             label: "Otros" },
];

const PERSONA_VACIA = {
  nombre: "", tipo_identificacion: "", numero_identificacion: "", edad: "", unidad_edad: "Años",
  esRenuente: false,
  presenta_evidencia: "", fotoA: null, fotoB: null, fotoPaiweb: null,
  tiene_vacuna_sarampion: "", se_aplico_vacuna: "", motivo_no_vacunada: "",
  nombre_vacunador: "", nombre_biologico: "", lote: "",
};

const esMenorDe5 = (c) => {
  const n = parseFloat(c.edad);
  if (isNaN(n)) return false;
  return c.unidad_edad === "Meses" ? n <= 60 : n <= 5;
};

export default function EditarPaiModal({ show, registro, onClose, onGuardado }) {
  const { usuario } = useBacAuth();
  const esAdmin = usuario?.rol === "admin";
  const [activandoAuto, setActivandoAuto] = useState(false);
  const [carnets, setCarnets] = useState([]);
  const [biologicoPendiente, setBiologicoPendiente] = useState("No");
  const [biologicoPendienteDetalle, setBiologicoPendienteDetalle] = useState("");
  const [nuevaPersona, setNuevaPersona] = useState(PERSONA_VACIA);
  const [editandoIdx, setEditandoIdx] = useState(null);
  const [comprimiendo, setComprimiendo] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [catalogos, setCatalogos] = useState({});
  const opcionesDe = (tipo) => catalogos[tipo] || [];

  // Mismo criterio que en el modal principal: fotos del carné solo a 6
  // años o menos.
  const requiereFotoCarnet = (c) => {
    const n = parseFloat(c.edad);
    if (isNaN(n)) return true;
    return c.unidad_edad === "Meses" ? true : n <= 6;
  };

  useEffect(() => {
    obtenerCatalogosCacheados().then(setCatalogos).catch(() => {});
  }, []);

  useEffect(() => {
    if (show && registro) {
      setCarnets((registro.carnets || []).map(c => ({ ...c })));
      setBiologicoPendiente(registro.biologico_pendiente || "No");
      setBiologicoPendienteDetalle(registro.biologico_pendiente_detalle || "");
      setNuevaPersona(PERSONA_VACIA);
      setEditandoIdx(null);
      setError("");
    }
  }, [show, registro]);

  if (!registro) return null;

  const totalVivienda = parseInt(registro.visita?.numero_personas_vivienda, 10) || carnets.length;

  const handleFoto = async (e, lado) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setComprimiendo(true);
    try {
      const comprimida = await comprimirImagen(file, { maxAncho: 1280, calidad: 0.7 });
      const base64 = await blobABase64(comprimida);
      const datos = { base64, nombre: file.name };
      setNuevaPersona(p => (
        lado === "a" ? { ...p, fotoA: datos } : lado === "b" ? { ...p, fotoB: datos } : { ...p, fotoPaiweb: datos }
      ));
    } catch {
      setError("No se pudo procesar la foto");
    } finally {
      setComprimiendo(false);
    }
  };

  const agregarPersona = () => {
    const c = nuevaPersona;

    if (c.esRenuente) {
      if (!c.nombre) {
        Swal.fire({ icon: "warning", title: "Falta al menos el nombre de la persona", timer: 2200, showConfirmButton: false });
        return;
      }
      const entradaRenuente = {
        id: editandoIdx !== null ? carnets[editandoIdx]?.id : undefined,
        nombre: c.nombre,
        tipo_identificacion: c.tipo_identificacion || "N/A",
        numero_identificacion: c.numero_identificacion || `RENUENTE-${Date.now()}`,
        edad: c.edad || null, unidad_edad: c.edad ? c.unidad_edad : null,
        presenta_evidencia: "No",
        tiene_vacuna_sarampion: null, se_aplico_vacuna: null, motivo_no_vacunada: null,
        nombre_vacunador: null, nombre_biologico: null, lote: null,
        fotoA_base64: null, fotoB_base64: null, fotoPaiweb_base64: null,
      };
      if (editandoIdx !== null) {
        setCarnets(p => { const arr = [...p]; arr[editandoIdx] = entradaRenuente; return arr; });
        setEditandoIdx(null);
      } else {
        setCarnets(p => [...p, entradaRenuente]);
      }
      setNuevaPersona(PERSONA_VACIA);
      return;
    }

    if (!c.nombre || !c.tipo_identificacion || !c.numero_identificacion || !c.edad || !c.presenta_evidencia) {
      Swal.fire({ icon: "warning", title: "Completa nombre, documento, edad y si presenta evidencia", timer: 2200, showConfirmButton: false });
      return;
    }
    if (c.presenta_evidencia === "Si") {
      const yaTieneFotoA = c.fotoA?.base64 || c.foto_a_url;
      const yaTieneFotoB = c.fotoB?.base64 || c.foto_b_url;
      const yaTienePaiweb = c.fotoPaiweb?.base64 || c.foto_paiweb_url;
      const n = parseFloat(c.edad);
      const requiereFoto = isNaN(n) ? true : (c.unidad_edad === "Meses" ? true : n <= 6);
      if (requiereFoto && !yaTienePaiweb && (!yaTieneFotoA || !yaTieneFotoB)) {
        Swal.fire({ icon: "warning", title: "Faltan las 2 caras del carnet, o en su lugar la foto de validación PAIWEB", timer: 2400, showConfirmButton: false });
        return;
      }
      if (!c.tiene_vacuna_sarampion) {
        Swal.fire({ icon: "warning", title: "Falta si ya tiene la vacuna de sarampión", timer: 2200, showConfirmButton: false });
        return;
      }
      if (c.tiene_vacuna_sarampion === "No") {
        if (!c.se_aplico_vacuna) {
          Swal.fire({ icon: "warning", title: "Falta si se le aplicó la vacuna", timer: 2200, showConfirmButton: false });
          return;
        }
        if (c.se_aplico_vacuna === "Si" && (!c.nombre_vacunador || !c.nombre_biologico || !c.lote)) {
          Swal.fire({ icon: "warning", title: "Faltan datos de vacunador, biológico y lote", timer: 2200, showConfirmButton: false });
          return;
        }
        if (c.se_aplico_vacuna === "No" && !c.motivo_no_vacunada) {
          Swal.fire({ icon: "warning", title: "Falta el motivo por el que no se vacunó", timer: 2200, showConfirmButton: false });
          return;
        }
      }
    }

    const entrada = {
      id: editandoIdx !== null ? carnets[editandoIdx]?.id : undefined,
      nombre: c.nombre, tipo_identificacion: c.tipo_identificacion, numero_identificacion: c.numero_identificacion,
      edad: c.edad, unidad_edad: c.unidad_edad, presenta_evidencia: c.presenta_evidencia,
      tiene_vacuna_sarampion: c.presenta_evidencia === "Si" ? c.tiene_vacuna_sarampion : null,
      se_aplico_vacuna: c.tiene_vacuna_sarampion === "No" ? c.se_aplico_vacuna : null,
      motivo_no_vacunada: c.se_aplico_vacuna === "No" ? c.motivo_no_vacunada : null,
      nombre_vacunador: c.se_aplico_vacuna === "Si" ? c.nombre_vacunador : null,
      nombre_biologico: c.se_aplico_vacuna === "Si" ? c.nombre_biologico : null,
      lote: c.se_aplico_vacuna === "Si" ? c.lote : null,
      // Fotos: nuevas (base64) tienen prioridad; si no cambió, conserva la URL que ya tenía.
      fotoA_base64: c.fotoA?.base64 || null,
      fotoB_base64: c.fotoB?.base64 || null,
      fotoPaiweb_base64: c.fotoPaiweb?.base64 || null,
      foto_a_url: c.fotoA ? undefined : c.foto_a_url,
      foto_b_url: c.fotoB ? undefined : c.foto_b_url,
    };

    if (editandoIdx !== null) {
      setCarnets(p => { const arr = [...p]; arr[editandoIdx] = entrada; return arr; });
      setEditandoIdx(null);
    } else {
      setCarnets(p => [...p, entrada]);
    }
    setNuevaPersona(PERSONA_VACIA);
  };

  const editarPersona = (idx) => {
    const c = carnets[idx];
    const eraRenuente = c.presenta_evidencia !== "Si" && (c.tipo_identificacion === "N/A" || String(c.numero_identificacion || "").startsWith("RENUENTE-"));
    setNuevaPersona({
      nombre: c.nombre,
      tipo_identificacion: eraRenuente ? "" : c.tipo_identificacion,
      numero_identificacion: eraRenuente ? "" : c.numero_identificacion,
      edad: c.edad, unidad_edad: c.unidad_edad || "Años", presenta_evidencia: c.presenta_evidencia,
      esRenuente: eraRenuente,
      tiene_vacuna_sarampion: c.tiene_vacuna_sarampion || "", se_aplico_vacuna: c.se_aplico_vacuna || "",
      motivo_no_vacunada: c.motivo_no_vacunada || "", nombre_vacunador: c.nombre_vacunador || "",
      nombre_biologico: c.nombre_biologico || "", lote: c.lote || "",
      fotoA: null, fotoB: null, fotoPaiweb: null,
      foto_a_url: c.foto_a_url, foto_b_url: c.foto_b_url, foto_paiweb_url: c.foto_paiweb_url,
    });
    setEditandoIdx(idx);
  };

  // Revisa cada persona de la lista (no solo la que está abierta en el
  // mini-formulario) con las mismas reglas de siempre — es lo que evita
  // que un registro viejo/incompleto se cuele sin nadie notarlo.
  const encontrarPersonaIncompleta = () => {
    for (const c of carnets) {
      const esRenuenteGuardado = c.presenta_evidencia !== "Si";
      if (esRenuenteGuardado) continue; // un renuente de verdad no necesita más datos

      const tieneFotoA = c.fotoA_base64 || c.foto_a_url;
      const tieneFotoB = c.fotoB_base64 || c.foto_b_url;
      const tienePaiweb = c.fotoPaiweb_base64 || c.foto_paiweb_url;
      const nEdad = parseFloat(c.edad);
      const requiereFoto = isNaN(nEdad) ? true : (c.unidad_edad === "Meses" ? true : nEdad <= 6);
      if (requiereFoto && !tienePaiweb && (!tieneFotoA || !tieneFotoB)) {
        return { persona: c, motivo: "le faltan las 2 caras del carnet (o la foto de PAIWEB)" };
      }
      if (!c.tiene_vacuna_sarampion) {
        return { persona: c, motivo: "no se respondió si ya tiene la vacuna de sarampión" };
      }
      if (c.tiene_vacuna_sarampion === "No") {
        if (!c.se_aplico_vacuna) {
          return { persona: c, motivo: "no se respondió si se le aplicó la vacuna" };
        }
        if (c.se_aplico_vacuna === "Si" && (!c.nombre_vacunador || !c.nombre_biologico || !c.lote)) {
          return { persona: c, motivo: "faltan datos de vacunador, biológico o lote" };
        }
        if (c.se_aplico_vacuna === "No" && !c.motivo_no_vacunada) {
          return { persona: c, motivo: "falta el motivo por el que no se vacunó" };
        }
      }
    }
    return null;
  };

  const quitarPersona = (idx) => {
    if (editandoIdx === idx) { setNuevaPersona(PERSONA_VACIA); setEditandoIdx(null); }
    setCarnets(p => p.filter((_, i) => i !== idx));
  };

  const activarAutomatica = async () => {
    const confirmado = await Swal.fire({
      icon: "warning",
      title: "¿Activar el cálculo automático?",
      html: `Esto recalcula TODA la matriz de esta vivienda a partir de las ${carnets.length} persona(s) que están cargadas ahora mismo — y a partir de acá, cada vez que edites a alguien, se va a recalcular sola.<br><br><strong>Solo hazlo si estás seguro de que ya tienes a todas las personas de la vivienda cargadas una por una.</strong>`,
      showCancelButton: true, confirmButtonText: "Sí, activar", cancelButtonText: "Cancelar", confirmButtonColor: "#16342a",
    });
    if (!confirmado.isConfirmed) return;
    setActivandoAuto(true);
    try {
      await activarMatrizAutomatica(registro.id);
      Swal.fire({ icon: "success", title: "Cálculo automático activado", timer: 1800, showConfirmButton: false });
      onGuardado();
      onClose();
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "No se pudo activar", "error");
    } finally {
      setActivandoAuto(false);
    }
  };

  const guardar = async () => {
    const incompleta = encontrarPersonaIncompleta();
    if (incompleta) {
      Swal.fire({
        icon: "warning",
        title: `${incompleta.persona.nombre || "Una persona"} quedó incompleta`,
        text: `${incompleta.motivo}. Ábrela con ✏️ y complétala antes de guardar.`,
        confirmButtonColor: "#16342a",
      });
      return;
    }

    if (carnets.length !== totalVivienda) {
      // Puede ser un registro de antes de este cambio, con menos gente
      // caracterizada de la que dice el total — eso no debe bloquear la
      // edición para siempre. Se avisa, pero se deja guardar igual.
      const confirmado = await Swal.fire({
        icon: "warning",
        title: "El total no coincide",
        text: `La vivienda tiene ${totalVivienda} persona(s) y hay ${carnets.length} caracterizada(s). ¿Guardar igual?`,
        showCancelButton: true, confirmButtonText: "Guardar igual", cancelButtonText: "Cancelar", confirmButtonColor: "#b7791f",
      });
      if (!confirmado.isConfirmed) return;
    }
    setError("");
    setGuardando(true);
    try {
      const payload = {
        biologico_pendiente: biologicoPendiente,
        biologico_pendiente_detalle: biologicoPendienteDetalle || null,
        carnets: carnets.map(c => ({
          id: c.id, nombre: c.nombre, tipo_identificacion: c.tipo_identificacion, numero_identificacion: c.numero_identificacion,
          edad: c.edad, unidad_edad: c.unidad_edad, presenta_evidencia: c.presenta_evidencia,
          tiene_vacuna_sarampion: c.tiene_vacuna_sarampion, se_aplico_vacuna: c.se_aplico_vacuna,
          motivo_no_vacunada: c.motivo_no_vacunada, nombre_vacunador: c.nombre_vacunador,
          nombre_biologico: c.nombre_biologico, lote: c.lote,
        })),
      };
      const res = await editarPaiVacunacion(registro.id, payload);

      // Fotos nuevas (las que sí cambiaron) se suben aparte, igual que en creación.
      const codigoVivienda = registro.visita?.codigo_vivienda;
      for (const c of carnets) {
        if (!c.fotoA && !c.fotoB && !c.fotoPaiweb) continue;
        const carnetReal = (res.data.carnets || []).find(x => x.numero_identificacion === c.numero_identificacion);
        if (!carnetReal) continue;
        for (const [lado, foto] of [["a", c.fotoA], ["b", c.fotoB], ["paiweb", c.fotoPaiweb]]) {
          if (!foto) continue;
          const fd = new FormData();
          fd.append("archivo", await (await fetch(foto.base64)).blob(), foto.nombre);
          const resSubida = await subirFotoVivienda(fd);
          await adjuntarFotoCarnet(codigoVivienda, c.numero_identificacion, lado, { foto_url: resSubida.data.url, foto_nombre: resSubida.data.nombre });
        }
      }

      Swal.fire({ icon: "success", title: "Cambios guardados", timer: 1600, showConfirmButton: false });
      onGuardado();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "No se pudo guardar — revisa tu conexión");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered scrollable size="lg">
      <Modal.Header closeButton style={{ background: "linear-gradient(135deg, #16342a, #2d6a4f)", color: "#fff" }}>
        <Modal.Title style={{ fontSize: "1rem" }}>✏️ Editar PAI — {registro.visita?.codigo_vivienda}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="p-2 px-3 rounded mb-3 d-flex justify-content-between align-items-center" style={{ background: "#eef1e7" }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: "#16342a" }}>👪 Personas en la vivienda</span>
          <span className="badge" style={{ background: "#16342a", fontSize: 13 }}>{carnets.length} / {totalVivienda}</span>
        </div>

        {registro.origen_matriz === "manual" && (
          <div className="alert alert-secondary py-2 px-3 mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ fontSize: 12 }}>
            <span>
              🔒 <strong>Matriz cargada manualmente</strong> — los totales por grupo de edad de este registro NO se recalculan solos, para no pisar lo que ya está puesto. Puedes editar personas y otros datos con normalidad.
            </span>
            {esAdmin && (
              <Button variant="outline-dark" size="sm" onClick={activarAutomatica} disabled={activandoAuto} style={{ fontSize: 11.5, whiteSpace: "nowrap" }}>
                {activandoAuto ? "Activando..." : "🔄 Activar cálculo automático"}
              </Button>
            )}
          </div>
        )}

        <div className="p-3 rounded mb-3" style={{ background: "#f9fafb", border: "1px solid #dee2e6" }}>
          <div className="row g-2 mb-2">
            <div className="col-12 col-md-8">
              <Form.Label style={{ fontSize: 10.5 }}>Nombre</Form.Label>
              <Form.Control size="sm" value={nuevaPersona.nombre} onChange={e => setNuevaPersona(p => ({ ...p, nombre: e.target.value }))} />
            </div>
            <div className="col-12 col-md-4 d-flex align-items-end">
              <Button type="button" size="sm" className="w-100"
                variant={nuevaPersona.esRenuente ? "danger" : "outline-danger"}
                onClick={() => setNuevaPersona(p => ({ ...p, esRenuente: !p.esRenuente }))}>
                🚫 {nuevaPersona.esRenuente ? "Marcado como renuente" : "Renuente"}
              </Button>
            </div>
          </div>

          {nuevaPersona.esRenuente && (
            <div className="alert alert-warning py-2 px-3 mb-2" style={{ fontSize: 11.5 }}>
              ⚠️ Con el nombre alcanza — no hace falta documento, edad ni ningún otro dato.
            </div>
          )}

          {!nuevaPersona.esRenuente && (
            <>
          <div className="row g-2 mb-2">
            <div className="col-6 col-md-3">
              <Form.Label style={{ fontSize: 10.5 }}>Tipo doc</Form.Label>
              <Form.Select size="sm" value={nuevaPersona.tipo_identificacion} onChange={e => setNuevaPersona(p => ({ ...p, tipo_identificacion: e.target.value }))}>
                <option value="">Tipo doc...</option>
                {opcionesDe("tipos-documento").map(v => <option key={v} value={v}>{v}</option>)}
              </Form.Select>
            </div>
            <div className="col-6 col-md-3">
              <Form.Label style={{ fontSize: 10.5 }}>Documento</Form.Label>
              <Form.Control size="sm" value={nuevaPersona.numero_identificacion} onChange={e => setNuevaPersona(p => ({ ...p, numero_identificacion: e.target.value }))} />
            </div>
            <div className="col-6 col-md-1">
              <Form.Label style={{ fontSize: 10.5 }}>Edad</Form.Label>
              <Form.Control type="number" size="sm" value={nuevaPersona.edad} onChange={e => setNuevaPersona(p => ({ ...p, edad: e.target.value }))} />
            </div>
            <div className="col-6 col-md-1">
              <Form.Label style={{ fontSize: 10.5 }}>&nbsp;</Form.Label>
              <Form.Select size="sm" value={nuevaPersona.unidad_edad} onChange={e => setNuevaPersona(p => ({ ...p, unidad_edad: e.target.value }))}>
                <option value="Años">Años</option>
                <option value="Meses">Meses</option>
              </Form.Select>
            </div>
          </div>

          <div className="mb-2">
            <Form.Label style={{ fontSize: 11 }}>¿Presenta carnet o hay revisión por PAIWEB?</Form.Label>
            <div className="d-flex gap-2">
              {["Si", "No"].map(v => (
                <Button key={v} type="button" size="sm" className="flex-grow-1"
                  variant={nuevaPersona.presenta_evidencia === v ? (v === "Si" ? "success" : "danger") : "outline-secondary"}
                  onClick={() => setNuevaPersona(p => ({ ...p, presenta_evidencia: v }))}>{v}</Button>
              ))}
            </div>
          </div>

          {nuevaPersona.presenta_evidencia === "Si" && !requiereFotoCarnet(nuevaPersona) && (
            <div className="alert alert-secondary py-2 px-3 mb-2" style={{ fontSize: 11.5 }}>
              ℹ️ Con más de 6 años no hace falta foto del carnet.
            </div>
          )}

          {nuevaPersona.presenta_evidencia === "Si" && requiereFotoCarnet(nuevaPersona) && (
            <>
              <div className="row g-2 mb-2">
                {[
                  { lado: "a", label: "Cara A", foto: nuevaPersona.fotoA, url: nuevaPersona.foto_a_url },
                  { lado: "b", label: "Cara B", foto: nuevaPersona.fotoB, url: nuevaPersona.foto_b_url },
                  { lado: "paiweb", label: "Foto PAIWEB (opcional)", foto: nuevaPersona.fotoPaiweb, url: nuevaPersona.foto_paiweb_url },
                ].map(({ lado, label, foto, url }) => (
                  <div className="col-12 col-md-4" key={lado}>
                    <div className="d-flex align-items-center gap-2 p-2 rounded h-100" style={{ background: "#fff", border: "1px dashed #dee2e6" }}>
                      <div className="rounded d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 40, height: 40, background: "#eef1e7", overflow: "hidden" }}>
                        {foto ? <img src={foto.base64} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          : url ? <img src={`${API.defaults.baseURL}${url}`} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          : "🪪"}
                      </div>
                      <label className="btn btn-outline-secondary btn-sm mb-0" style={{ fontSize: 10.5 }}>
                        {comprimiendo ? "..." : label}
                        <input type="file" accept="image/*" onChange={e => handleFoto(e, lado)} disabled={comprimiendo} className="d-none" />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {nuevaPersona.presenta_evidencia === "Si" && (
            <>
              <div className="mb-2">
                <Form.Label style={{ fontSize: 11 }}>¿Ya tiene la vacuna de sarampión?</Form.Label>
                <div className="d-flex gap-2">
                  {["Si", "No"].map(v => (
                    <Button key={v} type="button" size="sm" className="flex-grow-1"
                      variant={nuevaPersona.tiene_vacuna_sarampion === v ? (v === "Si" ? "success" : "warning") : "outline-secondary"}
                      onClick={() => setNuevaPersona(p => ({ ...p, tiene_vacuna_sarampion: v, se_aplico_vacuna: "", motivo_no_vacunada: "", nombre_vacunador: "", nombre_biologico: "", lote: "" }))}>{v}</Button>
                  ))}
                </div>
              </div>

              {nuevaPersona.tiene_vacuna_sarampion === "No" && (
                <div className="mb-2">
                  <Form.Label style={{ fontSize: 11 }}>¿Se le aplicó ahora?</Form.Label>
                  <div className="d-flex gap-2">
                    {["Si", "No"].map(v => (
                      <Button key={v} type="button" size="sm" className="flex-grow-1"
                        variant={nuevaPersona.se_aplico_vacuna === v ? (v === "Si" ? "success" : "danger") : "outline-secondary"}
                        onClick={() => setNuevaPersona(p => ({ ...p, se_aplico_vacuna: v }))}>{v}</Button>
                    ))}
                  </div>
                </div>
              )}

              {nuevaPersona.se_aplico_vacuna === "Si" && (
                <div className="p-2 rounded mb-2" style={{ background: "#e4f2e8" }}>
                  <div className="row g-2">
                    <div className="col-12 col-md-4">
                      <Form.Label style={{ fontSize: 10.5 }}>Vacunador</Form.Label>
                      <Form.Control size="sm" value={nuevaPersona.nombre_vacunador} onChange={e => setNuevaPersona(p => ({ ...p, nombre_vacunador: e.target.value }))} />
                    </div>
                    <div className="col-6 col-md-4">
                      <Form.Label style={{ fontSize: 10.5 }}>Biológico</Form.Label>
                      <Form.Control size="sm" value={nuevaPersona.nombre_biologico} onChange={e => setNuevaPersona(p => ({ ...p, nombre_biologico: e.target.value }))} />
                    </div>
                    <div className="col-6 col-md-4">
                      <Form.Label style={{ fontSize: 10.5 }}>Lote</Form.Label>
                      <Form.Control size="sm" value={nuevaPersona.lote} onChange={e => setNuevaPersona(p => ({ ...p, lote: e.target.value }))} />
                    </div>
                  </div>
                </div>
              )}

              {nuevaPersona.se_aplico_vacuna === "No" && (
                <div className="mb-2">
                  <Form.Label style={{ fontSize: 10.5 }}>Motivo</Form.Label>
                  <Form.Select size="sm" value={nuevaPersona.motivo_no_vacunada} onChange={e => setNuevaPersona(p => ({ ...p, motivo_no_vacunada: e.target.value }))}>
                    <option value="">Selecciona...</option>
                    {MOTIVOS_SIMPLE.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </Form.Select>
                </div>
              )}
            </>
          )}
            </>
          )}

          <div className="d-flex gap-2 mt-2">
            <Button variant={editandoIdx !== null ? "primary" : "success"} type="button" size="sm" className="flex-grow-1" onClick={agregarPersona}>
              {editandoIdx !== null ? "💾 Guardar cambios" : "+ Agregar persona"}
            </Button>
            {editandoIdx !== null && (
              <Button variant="outline-secondary" type="button" size="sm" onClick={() => { setNuevaPersona(PERSONA_VACIA); setEditandoIdx(null); }}>Cancelar</Button>
            )}
          </div>
        </div>

        {carnets.length > 0 && (
          <div className="d-flex flex-column gap-2 mb-3">
            {carnets.map((c, idx) => {
              const eraRenuente = c.presenta_evidencia !== "Si" && (c.tipo_identificacion === "N/A" || String(c.numero_identificacion || "").startsWith("RENUENTE-"));
              const estado = eraRenuente ? { texto: "Renuente", color: "danger" }
                : c.presenta_evidencia !== "Si" ? { texto: "No presenta carnet", color: "danger" }
                : !c.tiene_vacuna_sarampion ? { texto: "⚠️ Incompleta", color: "dark" }
                : c.tiene_vacuna_sarampion === "Si" ? { texto: "Vacunada previa", color: "primary" }
                : c.se_aplico_vacuna === "Si" ? { texto: "Vacunada en barrido", color: "success" }
                : c.se_aplico_vacuna === "No" ? { texto: "No vacunada", color: "warning" }
                : { texto: "⚠️ Incompleta", color: "dark" };
              return (
                <div key={idx} className="d-flex justify-content-between align-items-center p-2 px-3 rounded" style={{ background: "#f5f6f0", fontSize: 12.5 }}>
                  <div className="d-flex align-items-center gap-2">
                    <span className={`badge bg-${estado.color}`}>{estado.texto}</span>
                    <span><strong>{c.nombre}</strong> — {c.edad} {c.unidad_edad} — {c.tipo_identificacion} {c.numero_identificacion}</span>
                  </div>
                  <div className="d-flex gap-2">
                    <button type="button" onClick={() => editarPersona(idx)} className="btn btn-link p-0" style={{ fontSize: 14, color: "#0C447C" }}>✏️</button>
                    <button type="button" onClick={() => quitarPersona(idx)} className="btn btn-link text-danger p-0" style={{ fontSize: 14 }}>✕</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {carnets.some(c => c.presenta_evidencia === "Si") && (
          <>
            <Form.Label className="fw-bold" style={{ fontSize: 12.5 }}>¿Biológico pendiente?</Form.Label>
            <div className="d-flex gap-2 mb-2">
              {["No", "Si"].map(v => (
                <Button key={v} type="button" className="flex-grow-1"
                  variant={biologicoPendiente === v ? (v === "Si" ? "danger" : "success") : "outline-secondary"}
                  onClick={() => setBiologicoPendiente(v)}>{v}</Button>
              ))}
            </div>
            {biologicoPendiente === "Si" && (
              <Form.Control placeholder="Ej: SRP 2ª dosis" value={biologicoPendienteDetalle} onChange={e => setBiologicoPendienteDetalle(e.target.value)} />
            )}
          </>
        )}

        {error && <Alert variant="danger" className="py-2 mt-3" style={{ fontSize: 12.5 }}>{error}</Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>Cancelar</Button>
        <Button variant="success" onClick={guardar} disabled={guardando} style={{ background: "#16342a", border: "none" }}>
          {guardando ? "Guardando..." : "Guardar cambios"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}